// useRelatedGrandchildNodes.js
//
// Loads configured N2 related nodes: host → throughType → tabType
// from `suggestedChildren[].alsoShowTabs`.

import { useEffect, useMemo, useState } from 'react';

import { listAdjacentNodeKeys } from 'services/nodes/NodeEdgeService';
import { getNodesByKeys } from 'services/nodes/NodeReadService';
import { getAlsoShowTabPaths } from 'utilities/nodeTypeRelatedLists';

function normalizeKey(value) {
  return String(value || '').trim();
}

function normalizeType(value) {
  return String(value || '').trim();
}

/**
 * useRelatedGrandchildNodes
 *
 * @param {object} params
 * @param {object|null} params.hostNode
 * @param {object[]} params.childNodes - Direct (N1) related nodes already loaded.
 * @param {object|null} params.hostTypeDef - Host type definition (suggestedChildren).
 * @param {string|null} [params.baseKey]
 * @returns {object[]} Grandchild nodes for opted-in alsoShowTabs.
 */
export default function useRelatedGrandchildNodes({
  hostNode = null,
  childNodes = [],
  hostTypeDef = null,
  baseKey = null,
} = {}) {
  const [grandchildNodes, setGrandchildNodes] = useState([]);

  const paths = useMemo(
    () => getAlsoShowTabPaths(hostTypeDef?.suggestedChildren),
    [hostTypeDef?.suggestedChildren],
  );

  const resolvedBaseKey = normalizeKey(baseKey || hostNode?.baseKey);
  const hostKey = normalizeKey(hostNode?.key);

  const pathSignature = useMemo(
    () => paths.map((path) => `${path.throughType}>${path.tabType}`).join('|'),
    [paths],
  );

  const intermediateSignature = useMemo(() => {
    if (!paths.length) return '';
    const throughTypes = new Set(paths.map((path) => path.throughType));
    return (Array.isArray(childNodes) ? childNodes : [])
      .filter((node) => throughTypes.has(normalizeType(node?.type)))
      .map((node) => normalizeKey(node?.key))
      .filter(Boolean)
      .sort()
      .join(',');
  }, [childNodes, paths]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!hostKey || !resolvedBaseKey || !paths.length) {
        if (!cancelled) setGrandchildNodes([]);
        return;
      }

      const throughTypeToTabTypes = new Map();
      paths.forEach((path) => {
        const throughType = normalizeType(path.throughType);
        const tabType = normalizeType(path.tabType);
        if (!throughType || !tabType) return;
        const set = throughTypeToTabTypes.get(throughType) || new Set();
        set.add(tabType);
        throughTypeToTabTypes.set(throughType, set);
      });

      const intermediates = (Array.isArray(childNodes) ? childNodes : []).filter((node) => (
        Boolean(normalizeKey(node?.key))
        && throughTypeToTabTypes.has(normalizeType(node?.type))
      ));

      if (!intermediates.length) {
        if (!cancelled) setGrandchildNodes([]);
        return;
      }

      try {
        const neighborLists = await Promise.all(
          intermediates.map((node) => listAdjacentNodeKeys(node.key, {
            baseKey: resolvedBaseKey,
          })),
        );

        const n1Keys = new Set(
          (Array.isArray(childNodes) ? childNodes : [])
            .map((node) => normalizeKey(node?.key))
            .filter(Boolean),
        );

        const allowedKeys = new Set();
        intermediates.forEach((intermediate, index) => {
          const tabTypes = throughTypeToTabTypes.get(normalizeType(intermediate?.type));
          if (!tabTypes) return;
          const neighbors = Array.isArray(neighborLists[index]) ? neighborLists[index] : [];
          neighbors.forEach((key) => {
            const normalized = normalizeKey(key);
            if (!normalized || normalized === hostKey || n1Keys.has(normalized)) return;
            allowedKeys.add(normalized);
          });
        });

        if (!allowedKeys.size) {
          if (!cancelled) setGrandchildNodes([]);
          return;
        }

        const loaded = await getNodesByKeys([...allowedKeys]);
        const next = (Array.isArray(loaded) ? loaded : []).filter((node) => {
          if (!node?.key || node.isTrashed) return false;
          const key = normalizeKey(node.key);
          if (!allowedKeys.has(key)) return false;
          const tabType = normalizeType(node.type);
          // Must match a configured tab type for at least one intermediate neighbor.
          return intermediates.some((intermediate, index) => {
            const tabTypes = throughTypeToTabTypes.get(normalizeType(intermediate?.type));
            if (!tabTypes || !tabTypes.has(tabType)) return false;
            const neighbors = Array.isArray(neighborLists[index]) ? neighborLists[index] : [];
            return neighbors.some((neighborKey) => normalizeKey(neighborKey) === key);
          });
        });

        if (!cancelled) setGrandchildNodes(next);
      } catch (error) {
        console.error('[useRelatedGrandchildNodes] failed to load N2 related nodes', error);
        if (!cancelled) setGrandchildNodes([]);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    childNodes,
    hostKey,
    intermediateSignature,
    pathSignature,
    paths,
    resolvedBaseKey,
  ]);

  return grandchildNodes;
}
